import { Anchor, Breadcrumbs, Button, Card, Container, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { LuArrowLeft, LuSave } from 'react-icons/lu'
import { PageLoader } from '@/frontend/components/PageLoader'

export const Route = createFileRoute('/portal/profile')({
  beforeLoad: async ({ context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ['auth', 'session'],
      queryFn: () => fetch('/api/auth/session', { credentials: 'include' }).then((r) => r.json()),
    })
    if (!data?.user) throw redirect({ to: '/login' })
    if (['SUPER_ADMIN', 'MANAGER', 'STAFF', 'VIEWER'].includes(data.user.role)) throw redirect({ to: '/dashboard' })
  },
  component: ProfilePage,
})

type ProfileCustomer = {
  id: string
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  mobile: string | null
  address: string | null
  city: string | null
  country: string | null
  postalCode: string | null
  website: string | null
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function ProfilePage() {
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ['portal', 'me'],
    queryFn: () => api<{ customer: ProfileCustomer } | { error: string }>('/api/portal/me'),
    retry: false,
  })

  const update = useMutation({
    mutationFn: (data: Partial<ProfileCustomer>) =>
      api<{ customer: ProfileCustomer }>('/api/portal/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'me'] })
      notifications.show({ color: 'teal', message: 'Profile diperbarui' })
    },
    onError: (e: Error) => notifications.show({ color: 'red', title: 'Gagal', message: e.message }),
  })

  const [form, setForm] = useState({
    phone: '',
    mobile: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    website: '',
  })

  useEffect(() => {
    if (me.data && 'customer' in me.data) {
      const c = me.data.customer
      setForm({
        phone: c.phone ?? '',
        mobile: c.mobile ?? '',
        address: c.address ?? '',
        city: c.city ?? '',
        country: c.country ?? '',
        postalCode: c.postalCode ?? '',
        website: c.website ?? '',
      })
    }
  }, [me.data])

  if (me.isLoading) return <PageLoader />

  if (me.error || !me.data || 'error' in me.data) {
    return (
      <Container size="md" py="xl">
        <Text>Profile not found.</Text>
      </Container>
    )
  }

  const c = me.data.customer

  return (
    <Container size="md" py="xl">
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor component={Link} to="/portal" c="dimmed" size="sm">
            Portal
          </Anchor>
          <Text size="sm">Profile</Text>
        </Breadcrumbs>

        <Title order={2} ff="var(--font-display)" fw={600}>
          Profile
        </Title>

        <Card padding="lg" radius="md">
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <ReadOnly label="First Name" value={c.firstName} />
              <ReadOnly label="Last Name" value={c.lastName} />
              <ReadOnly label="Email" value={c.email ?? '—'} />
            </SimpleGrid>
            <Text size="xs" c="dimmed">
              Untuk mengubah nama atau email, hubungi staff marina.
            </Text>
          </Stack>
        </Card>

        <Card padding="lg" radius="md">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="md">
            Kontak & Alamat
          </Text>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.currentTarget.value }))}
              />
              <TextInput
                label="Mobile"
                value={form.mobile}
                onChange={(e) => setForm((p) => ({ ...p, mobile: e.currentTarget.value }))}
              />
            </SimpleGrid>
            <TextInput
              label="Address"
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.currentTarget.value }))}
            />
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <TextInput
                label="City"
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.currentTarget.value }))}
              />
              <TextInput
                label="Country"
                value={form.country}
                onChange={(e) => setForm((p) => ({ ...p, country: e.currentTarget.value }))}
              />
              <TextInput
                label="Postal Code"
                value={form.postalCode}
                onChange={(e) => setForm((p) => ({ ...p, postalCode: e.currentTarget.value }))}
              />
            </SimpleGrid>
            <TextInput
              label="Website"
              value={form.website}
              onChange={(e) => setForm((p) => ({ ...p, website: e.currentTarget.value }))}
            />
          </Stack>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Button component={Link} to="/portal" variant="default" leftSection={<LuArrowLeft size={16} />}>
            Kembali
          </Button>
          <Button
            color="laut"
            leftSection={<LuSave size={16} />}
            loading={update.isPending}
            onClick={() => update.mutate(form)}
          >
            Simpan
          </Button>
        </div>
      </Stack>
    </Container>
  )
}

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </div>
  )
}
